import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useBreadcrumbs } from "../context/BreadcrumbContext";
import { usePractice } from "../context/PracticeContext";
import { budgetApi } from "../api/budget";
import { PageSkeleton } from "../components/PageSkeleton";
import { EmptyState } from "../components/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DollarSign, Save, Loader2, AlertTriangle } from "lucide-react";

function dollars(cents: number): string {
  return (cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function AdminBudget() {
  const { setBreadcrumbs } = useBreadcrumbs();
  const queryClient = useQueryClient();
  const { selectedPracticeId } = usePractice();
  const [draftDollars, setDraftDollars] = useState("");
  const [feedback, setFeedback] = useState<string | null>(null);

  useEffect(() => {
    setBreadcrumbs([{ label: "Administration" }, { label: "Budget" }]);
  }, [setBreadcrumbs]);

  const queryKey = ["admin", "budget", selectedPracticeId] as const;
  const { data, isLoading } = useQuery({
    queryKey,
    queryFn: () => budgetApi.get(selectedPracticeId!),
    enabled: !!selectedPracticeId,
  });

  // Seed the input from the loaded budget on load and after a save (refetch).
  useEffect(() => {
    if (data) setDraftDollars(data.budgetCents > 0 ? (data.budgetCents / 100).toFixed(2) : "");
  }, [data]);

  const save = useMutation({
    mutationFn: (budgetCents: number) => {
      setFeedback(null);
      return budgetApi.set(selectedPracticeId!, budgetCents);
    },
    onSuccess: (status) => {
      setFeedback(
        status.unlimited
          ? "Saved — budget cleared (unlimited)."
          : `Saved — monthly budget set to $${dollars(status.budgetCents)}.`,
      );
      queryClient.invalidateQueries({ queryKey });
    },
    onError: (e: unknown) => setFeedback(`Save failed: ${e instanceof Error ? e.message : String(e)}`),
  });

  if (!selectedPracticeId) {
    return (
      <div className="p-6">
        <EmptyState icon={DollarSign} message="Select a practice to manage its budget." />
      </div>
    );
  }
  if (isLoading || !data) return <PageSkeleton variant="list" />;

  const overBudget = !data.withinBudget && !data.unlimited;
  const pct = Math.min(100, data.utilizationPercent);

  return (
    <div className="space-y-4 p-6 max-w-2xl mx-auto">
      <div>
        <h2 className="text-xl font-bold">Monthly Budget</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Cap this practice's autonomous agent spend per month. When the cap is reached, agent model
          calls are skipped (deterministic paths still run). Set to blank/0 for unlimited.
        </p>
      </div>

      {feedback && (
        <div className="rounded-md border border-border bg-muted/30 px-4 py-2 text-sm">{feedback}</div>
      )}

      {overBudget && (
        <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm">
          <AlertTriangle className="h-4 w-4 mt-0.5 text-destructive shrink-0" />
          <span>
            This practice has reached its monthly budget — autonomous agent model calls are paused
            until next month or until the budget is raised.
          </span>
        </div>
      )}

      <div className="rounded-lg border border-border bg-card p-4 space-y-4">
        {/* Spend vs budget */}
        <div>
          <div className="flex items-baseline justify-between text-sm">
            <span className="text-muted-foreground">Month-to-date spend</span>
            <span className="font-medium">
              ${dollars(data.spentCents)}
              {data.unlimited ? "" : ` / $${dollars(data.budgetCents)}`}
            </span>
          </div>
          {!data.unlimited && (
            <div className="mt-2 h-2 w-full rounded-full bg-muted overflow-hidden">
              <div
                className={overBudget ? "h-full bg-destructive" : "h-full bg-primary"}
                style={{ width: `${pct}%` }}
              />
            </div>
          )}
          <p className="mt-1 text-xs text-muted-foreground">
            {data.unlimited ? "No budget set (unlimited)." : `${data.utilizationPercent}% of budget used.`}
          </p>
        </div>

        {/* Set budget */}
        <div className="border-t border-border pt-4">
          <label htmlFor="budget-input" className="text-xs font-semibold text-muted-foreground">
            Monthly budget (USD)
          </label>
          <div className="mt-1 flex items-center gap-2">
            <div className="relative">
              <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">$</span>
              <Input
                id="budget-input"
                type="number"
                min="0"
                step="0.01"
                value={draftDollars}
                onChange={(e) => setDraftDollars(e.target.value)}
                placeholder="0.00 (unlimited)"
                className="w-40 pl-5"
                disabled={save.isPending}
              />
            </div>
            <Button
              size="sm"
              onClick={() => save.mutate(Math.round((parseFloat(draftDollars) || 0) * 100))}
              disabled={save.isPending}
            >
              {save.isPending ? (
                <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
              ) : (
                <Save className="h-3.5 w-3.5 mr-1.5" />
              )}
              Save
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
